/**
 * DetailsTab Component
 *
 * Displays detailed WHOIS information and origination/discovery details for seeds.
 * Shows domain registration data, IP information, and how the seed was discovered.
 *
 * @component
 */

import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';

import { NoData } from '@/components/ui/NoData';
import { Seed } from '@/types';

interface DetailsTabProps {
  /** The seed to display details for */
  seed: Seed;
  /** Formatted details string (WHOIS data or IP information) */
  displayDetails?: string;
  /** Information about how the seed was discovered/originated */
  originationInfo: string[];
}

/**
 * DetailsTab displays WHOIS/domain details and origination information
 *
 * For domains and IPs, shows detailed registration and infrastructure data.
 * Includes discovery source information (manual addition, integration discovery, etc.)
 *
 * @param _seed - The seed object containing identifier and metadata (reserved for future use)
 * @param displayDetails - Formatted details content (JSON or plain text)
 * @param originationInfo - Array of discovery source descriptions
 */
export function DetailsTab({
  seed: _seed,
  displayDetails,
  originationInfo,
}: DetailsTabProps) {
  if (!displayDetails) {
    return (
      <NoData
        icon={
          <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
        }
        title="No Information Collected"
      />
    );
  }

  return (
    <>
      <pre className="text-sm">{displayDetails}</pre>
      {originationInfo.length > 0 && (
        <div className="border-chariot-stroke mt-6 border-t pt-6">
          <div className="text-chariot-text-secondary flex flex-col gap-1 text-sm">
            {originationInfo.map((info, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="bg-chariot-stroke size-2 rounded-full" />
                {info}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
